import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUserStatusAction, type StatusAction } from '@/hooks/use-users';
import { LAST_ADMIN_MESSAGE, type AdminUser, type UserWithAccessSync } from '@/types/users';
import { ChangeRoleDialog } from './ChangeRoleDialog';
import { StartDateDialog } from './StartDateDialog';

interface UserActionsMenuProps {
  user: AdminUser;
  activeAdminCount: number;
}

type OpenDialog = 'role' | 'start-date' | 'deactivate' | 'reactivate' | null;

const SUCCESS_MESSAGES: Record<StatusAction, string> = {
  deactivate: 'User deactivated.',
  reactivate: 'User reactivated.',
  'access-sync': 'Workspace access is in sync.',
};

export function UserActionsMenu({ user, activeAdminCount }: UserActionsMenuProps): JSX.Element {
  const [open, setOpen] = useState<OpenDialog>(null);
  const statusAction = useUserStatusAction();
  // The sole active Admin can be neither demoted nor deactivated (§10.1); the server enforces it too.
  const isLastAdmin = user.role === 'admin' && user.status === 'active' && activeAdminCount <= 1;

  function run(action: StatusAction): void {
    statusAction.mutate(
      { id: user.id, action },
      {
        onSuccess: (result: UserWithAccessSync) => {
          setOpen(null);
          if (result.accessSync.status === 'ok') {
            toast.success(SUCCESS_MESSAGES[action]);
            return;
          }
          toast.warning(result.accessSync.message, {
            action: { label: 'Retry access sync', onClick: () => run('access-sync') },
          });
        },
        onError: () => setOpen(null),
      },
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${user.name}`}>
            <MoreHorizontal aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem disabled={isLastAdmin} onSelect={() => setOpen('role')}>
            {user.role === 'admin' ? 'Make User' : 'Make Admin'}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setOpen('start-date')}>Edit start date</DropdownMenuItem>
          {user.accessSyncIssue && <DropdownMenuItem onSelect={() => run('access-sync')}>Retry access sync</DropdownMenuItem>}
          <DropdownMenuSeparator />
          {user.status === 'active' ? (
            <DropdownMenuItem disabled={isLastAdmin} onSelect={() => setOpen('deactivate')} className="text-danger-fg focus:text-danger-fg">
              Deactivate
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => setOpen('reactivate')}>Reactivate</DropdownMenuItem>
          )}
          {isLastAdmin && <p className="px-3 py-1.5 text-xs text-muted-foreground">{LAST_ADMIN_MESSAGE}</p>}
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangeRoleDialog user={user} open={open === 'role'} onOpenChange={(o) => setOpen(o ? 'role' : null)} />
      <StartDateDialog user={user} open={open === 'start-date'} onOpenChange={(o) => setOpen(o ? 'start-date' : null)} />

      <ConfirmDialog
        open={open === 'deactivate'}
        onOpenChange={(o) => setOpen(o ? 'deactivate' : null)}
        title={`Deactivate ${user.name}?`}
        body={
          <>
            <span className="block">
              They&apos;re signed out everywhere in Clockit, any running timer is discarded without saving, and their workspace
              access to Clockit is removed.
            </span>
            {user.isSelf && <span className="block font-medium text-danger-fg">You&apos;ll be signed out immediately.</span>}
          </>
        }
        confirmLabel="Deactivate"
        destructive
        pending={statusAction.isPending}
        onConfirm={() => run('deactivate')}
      />
      <ConfirmDialog
        open={open === 'reactivate'}
        onOpenChange={(o) => setOpen(o ? 'reactivate' : null)}
        title={`Reactivate ${user.name}?`}
        body="They can sign in to Clockit again and their workspace access is restored."
        confirmLabel="Reactivate"
        pending={statusAction.isPending}
        onConfirm={() => run('reactivate')}
      />
    </>
  );
}
